import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import { Button } from "../../../components/ui/Button";
import { useApiInfiniteQuery, type Api } from "../../../services/api";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import { formatArticleDate } from "../../../domains/app/utils/renderDate";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import { authorLine } from "../../../domains/article/utils/authorLine";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useShareArticle } from "../../../domains/user/hooks/useShareArticle";
import { useArticleSwipeCache } from "../../../features/article/stores/useArticleSwipeCache";
import { seedArticleDetailCache } from "../../../features/article/hooks/useArticleData";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import type { PrivateRoom } from "../../../domains/room/privateRoom";
import RoomEventDisplay from "../../../components/room/RoomEventDisplay";
import { EventDisplayDestination } from "../../../../../api/src/shared/domain/room";
import { useRecordRoomVisit } from "../../../domains/room/utils/roomVisits";
import { gaEventSelectItemArticle } from "../../../tools/analytics/domains/article.analytics";
import { journalImpactFactor } from "../../../domains/journal/utils/journalImpactFactor";
import { DesktopRoomShell } from "./DesktopRoomShell";
import {
  OtherRoomsSection,
  RoomCollectionsSection,
  RoomTopicsSection,
  roomTopics,
} from "./DesktopRoomRail";

type RoomArticle =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

/**
 * A room's Discovery tab on a desktop (Figma "D3.explore.room.default"): the
 * articles Juisci selected for the room, newest first, beside the rail. The
 * partner's own resources never enter this list: they have their tab.
 */
export function DesktopRoomDiscoveryPage({
  room,
}: {
  room: PrivateRoom;
}): React.JSX.Element {
  useRecordRoomVisit(room.id);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { contentLang } = useActiveLang();
  const { setDocs, setIndex } = useArticleSwipeCache();
  // `?topic=` is the chip picked in the rail. A topic the room does not
  // offer (an old link, a room re-curated since) filters nothing.
  const topicParam = new URLSearchParams(location.search).get("topic");
  const topics = roomTopics(room);
  const selectedTopic =
    topicParam !== null && topics.some((topic) => topic.key === topicParam)
      ? topicParam
      : null;
  const usesTopics = room.filterTopicModels.length > 0;
  const articlesQuery = useApiInfiniteQuery("/webapp/public/articles", {
    query: {
      limit: 20,
      sort: "default",
      language: contentLang,
      roomId: room.id,
      topics: usesTopics && selectedTopic !== null ? selectedTopic : undefined,
      topicsMatch:
        usesTopics && selectedTopic !== null ? ("any" as const) : undefined,
      tags: !usesTopics && selectedTopic !== null ? selectedTopic : undefined,
      tagsMatch:
        !usesTopics && selectedTopic !== null ? ("any" as const) : undefined,
      // The tag filter of a room not on topics yet also scopes to the room's
      // main specialty, as the mobile filter does.
      medical_specialties:
        !usesTopics &&
        selectedTopic !== null &&
        room.mainSpecialtyModel !== null
          ? room.mainSpecialtyModel.id
          : undefined,
    },
  });
  const articles = articlesQuery.state === "success" ? articlesQuery.docs : [];
  const loadMore =
    articlesQuery.state === "success" && articlesQuery.canFetchNextPage
      ? articlesQuery.fetchNextPage
      : null;

  // An invitation or a QR code that carries a collection lands on it, with
  // the router state the invitation set (the new member's welcome).
  const playlistParam = new URLSearchParams(location.search).get("playlist");
  if (playlistParam !== null)
    return (
      <Redirect
        to={{
          pathname: `/room/playlist/${room.id}/${encodeURIComponent(playlistParam)}`,
          state: location.state,
        }}
      />
    );

  function openArticle(article: RoomArticle, index: number): void {
    // The article's pager walks this list, and Back returns to it.
    setDocs(articles);
    setIndex(index);
    seedArticleDetailCache(article, room.id);
    gaEventSelectItemArticle({
      article,
      listName: AnalyticsItemList.ROOM_DISCOVERY,
    });
    history.push(`/room/post/${room.id}/${article.slug}`, {
      roomList: room.id,
    });
  }

  return (
    <DesktopRoomShell
      room={room}
      tab="discovery"
      rail={
        <>
          <RoomTopicsSection room={room} selectedTopic={selectedTopic} />
          <RoomCollectionsSection room={room} />
          <OtherRoomsSection room={room} />
        </>
      }
    >
      <RoomEventDisplay
        roomId={room.id}
        roomTextColor={null}
        pageType={EventDisplayDestination.DISCOVERY}
      />
      <div className="flex items-baseline gap-2 border-b border-ds-slate-100 pb-2">
        <h2 className="m-0 text-heading-xs font-semibold text-ds-black">
          {t("Latest")}
        </h2>
        <span className="text-footnote text-ds-slate-600">
          {t("Selected by Juisci")}
        </span>
      </div>
      {articlesQuery.state === "pending" ? (
        Array.from({ length: 6 }, (_, index) => (
          <DesktopArticleRowSkeleton key={index} />
        ))
      ) : articlesQuery.state === "error" ? (
        <p className="m-0 py-6 text-ui-body text-ds-slate-600">
          {t("Something went wrong. Please try again.")}
        </p>
      ) : articles.length === 0 ? (
        <p className="m-0 py-6 text-ui-body text-ds-slate-600">
          {t("No content available yet for this room")}
        </p>
      ) : (
        <>
          {/* Figma "Ready for dev": a subtle divider between rows and
              under the last, as in the other desktop lists. */}
          <div className="flex flex-col divide-y divide-ds-slate-100 border-b border-ds-slate-100">
            {articles.map((article, index) => (
              <RoomArticleRow
                key={article.id}
                article={article}
                onOpen={() => {
                  openArticle(article, index);
                }}
              />
            ))}
          </div>
          {loadMore === null ? null : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4 self-center"
              disabled={articlesQuery.isFetching}
              onClick={loadMore}
            >
              {t("Show more")}
            </Button>
          )}
        </>
      )}
    </DesktopRoomShell>
  );
}

function RoomArticleRow({
  article,
  onOpen,
}: {
  article: RoomArticle;
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { triggerShare } = useShareArticle(AnalyticsItemList.ROOM_DISCOVERY);
  const loadedAudioId = useLoadedAudioId();
  const journal = article.journal;
  const cardImage = article.cardImage;
  return (
    // Figma "Ready for dev": the room's list is the DesktopArticleRow
    // Collection line, the authors under the title.
    <DesktopArticleRow
      onRemove={null}
      layout="collection"
      title={article.title}
      author={authorLine(article.primary_author, article.authors)}
      description={null}
      date={formatArticleDate(article.publication_date, activeLang)}
      readingMinutes={null}
      meta={
        article.article_type === null
          ? t("Article")
          : getArticleTypeText(article.article_type)
      }
      metaVariant={getArticleTypeVariant(article.article_type)}
      access={article.isOpenAccess === true ? "open" : "closed"}
      imageUrl={
        cardImage === null
          ? null
          : cardImage.sizes === null
            ? cardImage.url
            : cardImage.sizes.medium.file
      }
      isInPlayer={loadedAudioId === article.id}
      bookmark={
        <DesktopSaveButton
          article={article}
          analyticsListName={AnalyticsItemList.ROOM_DISCOVERY}
          size="row"
        />
      }
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(article, currentUser);
            }
      }
      onClick={onOpen}
      source={
        journal === null
          ? null
          : {
              kind: "journal",
              name: journal.name,
              impactFactor: journalImpactFactor(journal),
              onClick: () => {
                history.push(`/journal/${journal.uid}`);
              },
            }
      }
    />
  );
}
